import React, { useState } from 'react';
import { Check, X, Eye, EyeOff, MapPin, Loader2 } from 'lucide-react';

const AccountCreationForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation helpers
  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
      case 'lastName':
        return value.length >= 2 && /^[A-Za-z]+$/.test(value);
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'phoneNumber':
        return /^\+?[1-9]\d{9,14}$/.test(value);
      case 'address':
        return value.length === 0 || value.length >= 10;
      case 'password':
        return value.length >= 8 && 
               /[A-Z]/.test(value) && 
               /[a-z]/.test(value) && 
               /[0-9]/.test(value) && 
               /[!@#$%^&*]/.test(value);
      case 'confirmPassword':
        return value === formData.password;
      default:
        return true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    const newErrors = { ...errors };
    if (name !== 'address') {
      if (!validateField(name, value)) {
        newErrors[name] = true;
      } else {
        delete newErrors[name];
      }
    }
    setErrors(newErrors);
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setErrors({}); // Reset errors before validation

    const newErrors = {};
    Object.keys(formData).forEach(key => {
        if (key !== 'address' && !validateField(key, formData[key])) {
            newErrors[key] = true;
        }
    });

    if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
    }

    setIsSubmitting(true);
    try {
        const response = await fetch('/auth/rgst', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (response.ok) {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                address: '',
                password: '',
                confirmPassword: '',
                phoneNumber: '',
            });
            alert(`${result.message || 'Your account has been created successfully.'}`);
            setErrors({});  // Ensure errors are cleared
        } else {
            setErrors({ submit: result.error || 'An unknown error occurred' });
            setTimeout(() => {
              setErrors({});  // Clear errors after 5 seconds
          }, 5000);
        }
    } catch (error) {
        setErrors({ submit: 'Submission failed, please try again later.' });
        setTimeout(() => {
          setErrors({});  // Clear errors after 5 seconds
      }, 5000);
    } finally {
        setTimeout(() => {
            setErrors({});  // Clear errors after 5 seconds
        }, 5000);
        setIsSubmitting(false);
    }
};


  return (
    <div className="max-w-xl mx-auto bg-gray-800 p-8 rounded-xl space-y-6">
      <h2 className="text-3xl font-light text-white text-center">
        Create Your Account
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Two-Column Layout Sections */}
        <div className="grid grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            <label className="block text-gray-300 mb-2">First Name</label>
            <div className="relative">
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                autoComplete='off'
                onChange={handleChange}
                className={`
                  w-full p-2 rounded-lg 
                  bg-gray-700 text-white
                  ${errors.firstName ? 'border-2 border-red-500' : 'border border-gray-600'}
                  focus:outline-none focus:ring-2 
                  ${errors.firstName ? 'focus:ring-red-500' : 'focus:ring-blue-500'}
                `}
                placeholder="John"
                required
              />
              {formData.firstName && (
                errors.firstName ? (
                  <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                ) : (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                )
              )}
            </div>
            {errors.firstName && (
              <p className="text-red-400 text-sm mt-1">
                Min 2 letters only
              </p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-gray-300 mb-2">Last Name</label>
            <div className="relative">
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete='off'
                className={`
                  w-full p-2 rounded-lg 
                  bg-gray-700 text-white
                  ${errors.lastName ? 'border-2 border-red-500' : 'border border-gray-600'}
                  focus:outline-none focus:ring-2 
                  ${errors.lastName ? 'focus:ring-red-500' : 'focus:ring-blue-500'}
                `}
                placeholder="Doe"
                required
              />
              {formData.lastName && (
                errors.lastName ? (
                  <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                ) : (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                )
              )}
            </div>
            {errors.lastName && (
              <p className="text-red-400 text-sm mt-1">
                Min 2 letters only
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-300 mb-2">Email Address</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete='off'
                className={`
                  w-full p-2 rounded-lg 
                  bg-gray-700 text-white
                  ${errors.email ? 'border-2 border-red-500' : 'border border-gray-600'}
                  focus:outline-none focus:ring-2 
                  ${errors.email ? 'focus:ring-red-500' : 'focus:ring-blue-500'}
                `}
                placeholder="john.doe@example.com"
                required
              />
              {formData.email && (
                errors.email ? (
                  <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                ) : (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                )
              )}
            </div>
            {errors.email && (
              <p className="text-red-400 text-sm mt-1">
                Invalid email
              </p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-gray-300 mb-2">Phone Number</label>
            <div className="relative">
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                autoComplete='off'
                onChange={handleChange}
                className={`
                  w-full p-2 rounded-lg 
                  bg-gray-700 text-white
                  ${errors.phoneNumber ? 'border-2 border-red-500' : 'border border-gray-600'}
                  focus:outline-none focus:ring-2 
                  ${errors.phoneNumber ? 'focus:ring-red-500' : 'focus:ring-blue-500'}
                `}
                placeholder="+1 (123) 456-7890"
                required
              />
              {formData.phoneNumber && (
                errors.phoneNumber ? (
                  <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                ) : (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                )
              )}
            </div>
            {errors.phoneNumber && (
              <p className="text-red-400 text-sm mt-1">
                Invalid phone number
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-300 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                autoComplete='off'
                className={`
                  w-full p-2 rounded-lg 
                  bg-gray-700 text-white
                  ${errors.password ? 'border-2 border-red-500' : 'border border-gray-600'}
                  focus:outline-none focus:ring-2 
                  ${errors.password ? 'focus:ring-red-500' : 'focus:ring-blue-500'}
                `}
                placeholder="Strong password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-400 text-sm mt-1">
                Weak password
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-gray-300 mb-2">Confirm Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                autoComplete='off'
                onChange={handleChange}
                className={`
                  w-full p-2 rounded-lg 
                  bg-gray-700 text-white
                  ${errors.confirmPassword ? 'border-2 border-red-500' : 'border border-gray-600'}
                  focus:outline-none focus:ring-2 
                  ${errors.confirmPassword ? 'focus:ring-red-500' : 'focus:ring-blue-500'}
                `}
                placeholder="Repeat password"
                required
              />
              {formData.confirmPassword && (
                errors.confirmPassword ? (
                  <X className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                ) : (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                )
              )}
            </div>
            {errors.confirmPassword && (
              <p className="text-red-400 text-sm mt-1">
                Passwords do not match
              </p>
            )}
          </div>
        </div>

        {/* Full-width Address */}
        <div>
          <label className="block text-gray-300 mb-2 flex items-center">
            <MapPin className="mr-2 text-gray-400" size={20} />
            Address (Optional)
          </label>
          <input
            type="text"
            name="address"
            value={formData.address}
            autoComplete='off'
            onChange={handleChange}
            className="
              w-full p-2 rounded-lg 
              bg-gray-700 text-white
              border border-gray-600
              focus:outline-none focus:ring-2 focus:ring-blue-500
            "
            placeholder="1234 Main St, Anytown, USA"
          />
        </div>

         
        {errors.submit && (
            <div className="text-red-500 text-center mt-2">
                {errors.submit}
            </div>
        )}

        {/* Submit Button */}
        <button
            type="submit"
            disabled={Object.keys(errors).length > 0 || isSubmitting}
            className={`
                w-full py-3 rounded-xl mt-4 transition-all
                ${Object.keys(errors).length === 0 && !isSubmitting 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'}
                flex items-center justify-center space-x-2
            `}
        >
            {isSubmitting ? (
                <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
                'Create Account'
            )}
        </button>
      </form>
    </div>
  );
};

export default AccountCreationForm;